import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWalletConnectStore } from '@/stores/walletConnect';
import { useMultisigStore } from '@/stores/multisig';
import { getConfig } from '@/lib/config';
import {
  decodeDappTransaction,
  runTransactionFlow,
  type TxFlowStatus,
} from '@/lib/transactionFlow';
import { getWalletKit } from '@/lib/walletConnect';
import { signedFetch } from '@/lib/signedFetch';
import { env } from '@/lib/env';
import Authenticate, { type AuthenticateSubmit } from '@/components/Authenticate';

export default function TransactionReview() {
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);

  const pending = useWalletConnectStore((s) => s.pending);
  const history = useWalletConnectStore((s) => s.history);
  const setStatusInStore = useWalletConnectStore((s) => s.setStatus);
  const activePda = useMultisigStore((s) => s.activePda);

  const request = useMemo(
    () =>
      pending.find((r) => r.id === id) ?? history.find((r) => r.id === id),
    [pending, history, id],
  );

  const [status, setStatus] = useState<TxFlowStatus | null>(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [finalSig, setFinalSig] = useState<string | null>(null);

  async function reject() {
    const kit = getWalletKit();
    if (!kit || !request) return;
    await kit.respondSessionRequest({
      topic: request.topic,
      response: {
        id: request.id,
        jsonrpc: '2.0',
        error: { code: 4001, message: 'User rejected' },
      },
    });
    setStatusInStore(request.id, 'rejected');
    navigate('/app/transactions');
  }

  async function authorize(payload: AuthenticateSubmit) {
    const kit = getWalletKit();
    if (!kit || !request) return;
    setBusy(true);
    setAuthError(null);
    try {
      const cfg = await getConfig();
      const params = request.params as { transaction?: string; message?: string };
      if (request.method === 'solana_signMessage') {
        throw new Error('solana_signMessage not yet supported in this build');
      }
      if (!params.transaction) throw new Error('missing transaction in WC params');

      const decoded = await decodeDappTransaction(
        params.transaction,
        env.solanaRpcUrl,
      );
      let auth: { mode: 'totp' | 'session' | 'recovery'; code: string } = {
        mode: payload.mode,
        code: payload.code,
      };
      if (payload.mode === 'totp' && payload.ttlMin) {
        const sessionRes = await signedFetch<{ token: string }>(
          'session.create',
          {
            fields: { 'Totp Code': payload.code, 'Ttl Min': payload.ttlMin },
          },
        );
        if (sessionRes.ok && sessionRes.data?.token) {
          auth = { mode: 'session', code: sessionRes.data.token };
        }
      }
      const result = await runTransactionFlow({
        innerMessage: decoded.message,
        addressLookupTables: decoded.addressLookupTables,
        rpcUrl: env.solanaRpcUrl,
        programId: cfg.programId,
        auth,
        onProgress: (s) => setStatus(s),
      });
      setFinalSig(result.executeSig);
      await kit.respondSessionRequest({
        topic: request.topic,
        response: {
          id: request.id,
          jsonrpc: '2.0',
          result: { signature: result.executeSig },
        },
      });
      setStatusInStore(request.id, 'done');
    } catch (err) {
      setAuthError((err as Error).message);
      if (request) setStatusInStore(request.id, 'failed');
    } finally {
      setBusy(false);
      setShowAuth(false);
    }
  }

  if (!request) {
    return <div className="text-sm text-ink-300">Request not found.</div>;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-display text-2xl font-semibold">Review request</h1>
        <p className="text-sm text-ink-300 mt-1">
          From{' '}
          <span className="font-mono">
            {request.origin ?? 'unknown origin'}
          </span>
        </p>
      </div>

      <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-5 space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="text-ink-400">Method</div>
          <div className="font-mono text-ink-100">{request.method}</div>
          <div className="text-ink-400">Multisig</div>
          <div className="font-mono text-ink-100">
            {activePda?.slice(0, 6)}…{activePda?.slice(-6)}
          </div>
          <div className="text-ink-400">Received</div>
          <div className="text-ink-100">
            {new Date(request.receivedAt).toLocaleString()}
          </div>
        </div>
        <details className="text-xs">
          <summary className="cursor-pointer text-ink-300">Raw payload</summary>
          <pre className="mt-2 overflow-x-auto rounded bg-ink-950/60 p-3 text-[10px] font-mono text-ink-300">
            {JSON.stringify(request.params, null, 2)}
          </pre>
        </details>
      </div>

      {status && (
        <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-5 text-sm space-y-2">
          {status.kind === 'reading_index' && (
            <div>Reading on-chain transaction index…</div>
          )}
          {status.kind === 'awaiting_user_signature' && (
            <div>Waiting for your wallet signature…</div>
          )}
          {status.kind === 'submitting_create' && (
            <div>Submitting create + vote tx…</div>
          )}
          {status.kind === 'awaiting_backend_signature' && (
            <div>Backend voting (TOTP-gated)…</div>
          )}
          {status.kind === 'submitting_execute' && (
            <div>Executing on-chain…</div>
          )}
          {status.kind === 'done' && (
            <div className="space-y-1 text-avo-300">
              <div>Done. Execute signature:</div>
              <div className="font-mono text-xs break-all">
                {status.executeSig}
              </div>
            </div>
          )}
        </div>
      )}

      {authError && (
        <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {authError}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void reject()}
          disabled={busy}
          className="flex-1 rounded-lg border border-ink-800 bg-ink-900/60 px-4 py-3 text-sm text-ink-100 hover:bg-ink-800/60 transition-colors"
        >
          Reject
        </button>
        <button
          type="button"
          onClick={() => setShowAuth(true)}
          disabled={busy || !!finalSig}
          className="flex-1 rounded-lg bg-avo-500 px-4 py-3 text-sm font-medium text-ink-950 hover:bg-avo-400 transition-colors disabled:opacity-50"
        >
          Approve with TOTP
        </button>
      </div>

      {showAuth && (
        <div className="fixed inset-0 bg-black/60 grid place-content-center z-40 p-4">
          <div className="bg-ink-900 rounded-2xl border border-ink-800 max-w-sm w-full p-6">
            <h2 className="font-semibold mb-3 text-ink-50">
              Authorize transaction
            </h2>
            <Authenticate
              busy={busy}
              errorMessage={authError}
              onSubmit={(p) => void authorize(p)}
              onCancel={() => setShowAuth(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
